import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { supabase } from '../lib/supabase';
import { AddIcon } from '../components/icons';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

// Define the structure of a wallet transaction based on your DB schema
type WalletTransaction = {
  id: string;
  created_at: string;
  type: 'deposit' | 'deduction';
  amount: number;
  status: string; // Assuming you have a status field
  reference_type: string | null;
  reference_id: string | null; // Could be SIM ID, Megsapp Ref, Manual Ref etc.
  balance_before: number;
  balance_after: number;
  // Add other relevant fields like description or details if they exist
  description?: string;
};

export function Wallet() {
  const { profile } = useAuth();
  const organizationId = profile?.organization_id;

  const [balance, setBalance] = useState<number | null>(null);
  const [managementFeeDue, setManagementFeeDue] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [depositInfoModalOpen, setDepositInfoModalOpen] = useState(false);
  const [organizationName, setOrganizationName] = useState<string>('');

  const fetchData = useCallback(async () => {
    if (!organizationId) {
      setError("Organization context not found.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch current balance, name, AND management_fee_due
      const { data: orgData, error: balanceError } = await supabase
        .from('organizations')
        .select('wallet_balance, name, management_fee_due')
        .eq('id', organizationId)
        .single();

      if (balanceError) throw balanceError;
      setBalance(orgData?.wallet_balance ?? 0);
      setManagementFeeDue(orgData?.management_fee_due ?? 0);
      setOrganizationName(orgData?.name || '');

      // Fetch transaction history
      const { data: transactionsData, error: transactionsError } = await supabase
        .from('wallet_transactions')
        .select('*') // Select all columns for now, adjust as needed
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(100); // Add a limit for performance

      if (transactionsError) throw transactionsError;
      setTransactions((transactionsData as WalletTransaction[]) || []);

    } catch (err: any) {
      console.error("Error fetching wallet data:", err);
      setError(err.message || 'Failed to load wallet information.');
      setBalance(null);
      setManagementFeeDue(null);
      setTransactions([]);
      setOrganizationName('');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return 'R0.00';
    return `R${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'yyyy/MM/dd, HH:mm:ss');
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Helper to create a readable description/reference
  const getTransactionReference = (tx: WalletTransaction): string => {
    if (tx.description) return tx.description;
    if (tx.reference_type === 'sim_topup' && tx.reference_id) {
        // Maybe fetch SIM ICCID/MSISDN based on reference_id in the future?
        return `SIM Top-up (Ref: ${tx.reference_id.substring(0, 8)}...)`;
    } 
    if (tx.reference_type === 'manual_deposit' && tx.reference_id) {
        return `Manual Deposit (Ref: ${tx.reference_id})`;
    }
    if (tx.reference_id) return `Ref: ${tx.reference_id}`;
    return '-';
  };

  const handleAddFunds = () => {
    setDepositInfoModalOpen(true);
  };

  const handleCloseDepositInfoModal = () => {
    setDepositInfoModalOpen(false);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Wallet
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddFunds}
        >
          Add Funds
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Paper sx={{ p: 3, flex: 1, minWidth: '250px' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Available for Top-ups
          </Typography>
          <Typography variant="h3">
            {loading && balance === null ? (
              <CircularProgress size={40} />
            ) : (
              formatCurrency(balance)
            )}
          </Typography>
        </Paper>

        <Paper sx={{ p: 3, flex: 1, minWidth: '250px', 
            ...(managementFeeDue !== null && managementFeeDue > 0 && { 
                border: '1px solid', 
                borderColor: 'error.main' 
            }) 
        }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Outstanding Management Fees
          </Typography>
          <Typography variant="h3" color={managementFeeDue !== null && managementFeeDue > 0 ? 'error.main' : 'inherit'}>
            {loading && managementFeeDue === null ? (
              <CircularProgress size={40} />
            ) : (
              formatCurrency(managementFeeDue)
            )}
          </Typography>
        </Paper>

        <Paper sx={{ p: 3, flex: 1, minWidth: '200px', display: { xs: 'none', md: 'block'} }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Recent Transactions
          </Typography>
          <Typography variant="h3">
            {loading ? <CircularProgress size={40} /> : transactions.length}
          </Typography>
        </Paper>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Reference/Details</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right">Balance After</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No transactions found
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        {formatDate(transaction.created_at)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.type === 'deposit' ? 'Deposit' : 'Deduction'}
                          color={transaction.type === 'deposit' ? 'success' : 'error'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {getTransactionReference(transaction)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          color={transaction.type === 'deposit' ? 'success.main' : 'error.main'}
                        >
                          {transaction.type === 'deposit' ? '+' : '-'}
                          {formatCurrency(transaction.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(transaction.balance_after)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.status}
                          size="small"
                          color={transaction.status === 'completed' ? 'success' : (transaction.status === 'pending' ? 'warning' : 'default')}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={depositInfoModalOpen} onClose={handleCloseDepositInfoModal} maxWidth="sm">
        <DialogTitle>Bank Deposit Information</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mt: 1 }}>
            Please use the following details for your bank transfer:
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Account Name:</Typography>
            <Typography variant="body1" gutterBottom>YOUR_ACCOUNT_NAME</Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>Bank Name:</Typography>
            <Typography variant="body1" gutterBottom>YOUR_BANK_NAME</Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>Branch Code:</Typography>
            <Typography variant="body1" gutterBottom>YOUR_BRANCH_CODE</Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>Account Type:</Typography>
            <Typography variant="body1" gutterBottom>YOUR_ACCOUNT_TYPE</Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>Account Number:</Typography>
            <Typography variant="body1" gutterBottom>YOUR_BANK_ACCOUNT_NUMBER</Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>Deposit Reference:</Typography>
            <Typography variant="body1" gutterBottom>
              {organizationName ? 
                <>Please use your organization name: <strong>{organizationName}</strong></> : 
                'Please use your unique organization name.'
              }
            </Typography>
          </Box>
          <Typography variant="caption" display="block" sx={{ mt: 3 }}>
            * Please ensure you use the correct deposit reference so we can allocate your funds promptly. 
            * Funds may take 1-2 business days to reflect.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDepositInfoModal} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
} 